// src/views/hrms/leaves/LeaveTable.jsx
import React from "react";
import { Check, X } from "lucide-react";
/* -------------------- status chip color (case-insensitive) -------------------- */
const chip = (statusRaw) => {
  const s = String(statusRaw || "").toLowerCase();
  return (
    {
      pending: "bg-amber-100 text-amber-700",
      approved: "bg-emerald-100 text-emerald-700",
      rejected: "bg-rose-100 text-rose-700",
      cancelled: "bg-gray-200 text-gray-700",
      canceled: "bg-gray-200 text-gray-700",
    }[s] || "bg-gray-100 text-gray-700"
  );
};

/* -------------------- employee label helpers -------------------- */
const employeeLabel = (row) => {
  const empId =
    row?.employee?.id ||
    row?.employee?._id ||
    row?.employeeId ||
    "-";

  console.log("Employee ID:", empId);

  // 1) explicit normalized name
  if (row?.employeeName && row.employeeName !== "-") return row.employeeName;

  // 2) populated object
  const e = row?.employee;
  if (e && typeof e === "object") {
    const name =
      e.name || [e.firstName, e.lastName].filter(Boolean).join(" ").trim();

    return name || e.code || e.email || empId;
  }

  // 3) fallback
  return empId;
};

const employeeIdText = (row) => {
  // prefer code if populated
  if (row?.employee && typeof row.employee === "object" && row.employee.code)
    return String(row.employee.code);
  if (row?.employeeId) return String(row.employeeId);
  return String(row?.employee || "");
};

/* -------------------- date formatter -------------------- */
const fmt = (d) => {
  const x = new Date(d);
  if (isNaN(x)) return "-";
  return x.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* -------------------- days inclusive (from..to) -------------------- */
const daysInclusive = (from, to) => {
  const a = new Date(from);
  const b = new Date(to || from);
  if (isNaN(a) || isNaN(b)) return "-";
  // strip time to local midnight to avoid DST/zone edges
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  const ms = b.getTime() - a.getTime();
  const d = Math.floor(ms / 86400000) + 1;
  return d > 0 ? d : 1;
};

/* -------------------- main table component -------------------- */
export default function LeaveTable({
  rows = [],
  onApprove = () => {},
  onReject = () => {},
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-white/10">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-gray-50 text-gray-600 dark:bg-white/5 dark:text-white/70">
          <tr>
            <th className="px-4 py-3">Employee</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Dates</th>
            <th className="px-4 py-3">Days</th>
            <th className="px-4 py-3">Reason</th>
            <th className="px-4 py-3 text-center">Status</th>
            <th className="px-4 py-3 text-center">Actions</th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan="7" className="px-4 py-6 text-center text-gray-500 dark:text-white">
                No leaves found.
              </td>
            </tr>
          ) : (
            rows.map((r) => {
              const rowId = String(r.id || r._id || "");
              // prefer normalized labels when available
              const statusRaw =
                (r.statusLabel && r.statusLabel.toLowerCase()) ||
                String(r.status || "").toLowerCase();
              const isPending = statusRaw === "pending";
              const typeText = r.typeLabel || r.type || "-";
              const from = r.from ?? r.fromDate;
              const to = r.to ?? r.toDate ?? r.from ?? r.fromDate;

              return (
                <tr
                  key={r._id}
                  className="border-t border-gray-100 dark:border-white/10 "
                >
                  {/* Employee */}
                  <td className="px-4 py-3 dark:text-white">
                    <div className="font-medium text-navy-700 dark:text-white">
                      {employeeLabel(r)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-white">
                      {employeeIdText(r)}
                    </div>
                  </td>

                  {/* Leave Type */}
                  <td className="px-4 py-3 dark:text-white">{typeText}</td>

                  {/* From / To */}
                  <td className="px-4 py-3 dark:text-white">
                    {fmt(from)} — {fmt(to)}
                  </td>

                  {/* Days (inclusive) */}
                  <td className="px-4 py-3 dark:text-white">{daysInclusive(from, to)}</td>

                  {/* Reason */}
                  <td
                    className="max-w-[260px] truncate px-4 py-3 dark:text-white"
                    title={r.reason || ""}
                  >
                    {r.reason ?? "-"}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 text-center dark:text-white">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-semibold ${chip(
                        statusRaw
                      )}`}
                      title={statusRaw || "-"}
                    >
                      {statusRaw
                        ? statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1)
                        : "-"}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-center">
                    {isPending ? (
                      <div className="inline-flex items-center gap-3">
                        <button
                          className="rounded-lg border bg-gradient-to-br from-green-100 to-green-200 p-2
               transition hover:from-green-200 hover:to-green-300
               hover:text-green-700"
                          onClick={() => onApprove(rowId)}
                          title="Approve"
                          aria-label="Approve leave"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          className="rounded-lg border bg-gradient-to-br from-red-100 to-red-200 p-2
               transition hover:from-red-200 hover:to-red-300
               hover:text-red-700"
                          onClick={() => onReject(rowId)}
                          title="Reject"
                          aria-label="Reject leave"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">—</span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
